/**
 * School Advanced-List Members API Contract
 *
 * GET /v1/schools/:school_public_id/lists/:list_id/members
 * Resolve the members of a Blackbaud advanced list at use-time, returning
 * each row tagged with a match status against our user roster plus a
 * summary banner (matched / unmatched / join key used).
 *
 * Resolution is live, not stored — see plan Decision 3.
 */

import { z } from 'zod'
import { PaginationMetaSchema } from '../common'
import {
  ListMemberSchema,
  ListMemberJoinKeySchema,
  ListMemberUnmatchedReasonsSchema,
  type ListMember,
  type ListMemberJoinKey,
  type ListMemberUnmatchedReasons
} from '../../schemas/entities/list-member'

export {
  ListMemberSchema,
  ListMemberJoinKeySchema,
  type ListMember,
  type ListMemberJoinKey,
  type ListMemberUnmatchedReasons
}

/**
 * Request — path params carry school_public_id + list_id; no body / no
 * server-side row pagination (resolution is whole-list, see plan U6).
 */
export const AdvancedListMembersRequestSchema = z.object({})

export type AdvancedListMembersRequest = z.infer<typeof AdvancedListMembersRequestSchema>

/**
 * Response data block. `truncated: true` means U3's defensive page cap
 * fired (current cap = 100 pages = 100k rows); Explorer should surface
 * that to the user.
 */
export const AdvancedListMembersResponseDataSchema = z.object({
  rows: z.array(ListMemberSchema),
  joinKey: ListMemberJoinKeySchema,
  joinKeyColumn: z.string().nullable(),
  matchedCount: z.number().min(0),
  unmatchedCount: z.number().min(0),
  unmatchedReasons: ListMemberUnmatchedReasonsSchema,
  pagesFetched: z.number().min(0),
  truncated: z.boolean()
})

export type AdvancedListMembersResponseData = z.infer<typeof AdvancedListMembersResponseDataSchema>

export const AdvancedListMembersResponseSchema = z.object({
  data: AdvancedListMembersResponseDataSchema,
  message: z.string(),
  meta: PaginationMetaSchema
})

export type AdvancedListMembersResponse = z.infer<typeof AdvancedListMembersResponseSchema>
